import { Card, CardMedia, CardContent, CardActions, Typography, Button, Link, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { Meal } from '../../models/Meal';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Foodstuff } from '../../models/Foodstuff';
import { isLocalMealPlaceholder, resolveMealImageUrl } from '../../common/mealMetrics';

type MealCardProps = {
  meal: Meal;
};
export function MealCard(props: MealCardProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { meal } = props;

  const aiGenerated = Boolean(meal.images && meal.images.length > 0 ? meal.images[0].aiGenerated : false);
  const [imageUrl, setImageUrl] = useState(() => resolveMealImageUrl(meal));
  const resolvedImageUrl = resolveMealImageUrl(meal);
  useEffect(() => {
    setImageUrl(resolvedImageUrl);
  }, [resolvedImageUrl]);
  const isPlaceholderImage = isLocalMealPlaceholder(imageUrl);

  const [showReceipt, setShowReceipt] = useState(false);
  const hasReceipt = Boolean(meal.recipeSteps && meal.recipeSteps.length > 0);


  const handleViewReceipt = () => {
    setShowReceipt(true);
  };

  const handleHideReceipt = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowReceipt(false);
  };

  const resolveMeasurementValueSymbol = (foodstuff: Foodstuff): string => {
    return foodstuff.measurementUnits?.find(mu => mu.selected)?.symbol || 'g'
  }

  const formatIngredientQuantity = (foodstuff: Foodstuff): string | null => {
    if (typeof foodstuff.weight === 'number' && foodstuff.weight > 0) {
      const amount = Number.isInteger(foodstuff.weight) ? foodstuff.weight.toFixed(0) : foodstuff.weight.toFixed(1);
      return `${amount}g`;
    }

    if (typeof foodstuff.value === 'number' && foodstuff.value > 0) {
      const amount = Number.isInteger(foodstuff.value) ? foodstuff.value.toFixed(0) : foodstuff.value.toFixed(1);
      return `${amount}${resolveMeasurementValueSymbol(foodstuff)}`;
    }

    return null;
  }

  const visibleFoodstuffs = (meal.foodstuffs || []).filter((fs) => formatIngredientQuantity(fs));

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography
        variant="h6"
        sx={{
          zIndex: 2,
          px: 2,
          pt: 2,
          lineHeight: 1.25,
          minHeight: 56,
        }}
        gutterBottom
      >
        <Link
          onClick={() => { navigate(`/meal/${meal.id}`) }}
          sx={{ cursor: 'pointer', fontWeight: 700 }}
        >
          {meal.name}
        </Link>
      </Typography>
      {aiGenerated ? (
        <Tooltip title={t('common.ai_generated_image')} placement="top">
          <CardMedia
            component="img"
            height="150"
            image={imageUrl}
            alt={meal.name}
            onError={() => setImageUrl('/mealPlaceholder.png')}
            sx={{
              position: 'relative',
              objectFit: isPlaceholderImage ? 'contain' : 'cover',
              backgroundColor: isPlaceholderImage ? '#F3EEE8' : undefined,
              p: isPlaceholderImage ? 1.5 : 0,
            }}
          />
        </Tooltip>
      ) : (
        <CardMedia
          component="img"
          height="150"
          image={imageUrl}
          alt={meal.name}
          onError={() => setImageUrl('/mealPlaceholder.png')}
          sx={{
            position: 'relative',
            objectFit: isPlaceholderImage ? 'contain' : 'cover',
            backgroundColor: isPlaceholderImage ? '#F3EEE8' : undefined,
            p: isPlaceholderImage ? 1.5 : 0,
          }}
        />
      )}
      <CardContent sx={{ flex: '1 1 auto' }}>
        {visibleFoodstuffs.map((fs, index) => (
          <Typography variant="body2" key={index}>
            {fs.name} {formatIngredientQuantity(fs)}
          </Typography>
        ))}

        {showReceipt && meal.recipeSteps && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {meal.recipeSteps.map((step, idx) => (
              <p key={idx}>{idx+1}. {step}</p>
            ))}
            <Button variant="text" onClick={handleHideReceipt} sx={{ textTransform: 'none', color: '#6F675F' }}>
              {t('meal_card.hide_recipe')}
            </Button>
          </Typography>
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        {!showReceipt && hasReceipt && (
          <Button variant="text" onClick={handleViewReceipt} sx={{ textTransform: 'none', color: '#7A4A2E', fontWeight: 600 }}>
            {t('meal_card.view_recipe')}
          </Button>
        )}
      </CardActions>
    </Card >
  );
}
